import { useEffect, useState } from 'react';
import PageLayout from '../common/page-layout/PageLayout';
import { useRouter } from 'next/router';
import { useCustomer } from '@/app/context/CustomerContext/useCustomer';
import { useAuth } from '@/app/context/AuthContext/useAuth';
import { getDaysFromNow } from '@/app/utils/dateUtils';
import BackHeader from './back-header/BackHeader';
import AccountItem from './account-item/AccountItem';
import CanceledBanner from './canceled-banner/CanceledBanner';
import UpgradeSubscription from './upgrade-subscription/UpgradeSubscription';
import ManageSubscription from './manage-subscription/ManageSubscription';
import DeleteAccount from './delete-account/DeleteAccount';
import { useFeedback } from '@/app/context/FeedbackContext/useFeedback';
import Link from 'next/link';
import { useModal } from '@/app/context/ModalContext/useModal';
import { useAnalytics } from '@/app/context/AnalyticsContext/useAnalytics';
import { supportedCountries } from '@/app/constants/SupportedCountries';
import { SubscriptionItemNames } from '@/app/constants/SubsciptionItemNames';

interface IProps {
  showKeyDialog?: boolean;
}

export default function Account(props: IProps) {
  const [onBehalfId, setOnBehalfId] = useState('');

  const router = useRouter();
  const { user, accessKey, rotateAccessKey, signInOnBehalf } = useAuth();
  const { openModal } = useModal();

  const { logCheckoutSession } = useCustomer();

  const { trackEvent, analyticsInstance } = useAnalytics();

  const { submitFeedback } = useFeedback();

  const {
    subscription,
    hasAnyStripeSubscription,
    isProEligible,
    isPro,
    country
  } = useCustomer();

  useEffect(() => {
    if (props.showKeyDialog) {
      openModal('limbiksKey');
    }
  }, []);

  useEffect(() => {
    if (
      user &&
      !user.isAnonymous &&
      subscription.isLoaded &&
      subscription.isCancelled
    ) {
      trackEvent(analyticsInstance, 'account_subscription_cancelled');
    } else if (
      user &&
      !user.isAnonymous &&
      subscription.isLoaded &&
      subscription.isPro
    ) {
      trackEvent(analyticsInstance, 'account_subscription_active');
    }
  }, [user, subscription]);

  // useEffect(() => {
  //   if (
  //     router.query &&
  //     router.query.interval &&
  //     subscription.isLoaded &&
  //     !subscription.isPro &&
  //     isProEligible
  //   ) {
  //     router.replace('/app/account', undefined, { shallow: true });

  //     // To ensure analytics are best representative
  //     // removing automatic checkout
  //     // maybe want to A/B test this in the future
  //     // createCheckoutSession(router.query.interval === 'month');
  //   }
  // }, [router, subscription, isProEligible]);

  useEffect(() => {
    if (subscription.isLoaded && !subscription.isPro && isProEligible) {
      router.replace('/app/account', undefined, { shallow: true });
      trackEvent(analyticsInstance, 'view_item', {
        currency: country.currencyCode,
        value:
          !router.query.interval || router.query.interval === 'month'
            ? country.monthlyRate
            : country.yearlyRate,
        upgrade_source: router.query.source ? router.query.source : 'none',
        items: [
          {
            item_name: SubscriptionItemNames.LimbiksProNewSubscription
          }
        ]
      });
      if (router.query.source) {
        submitFeedback(`Pro Clicked: ${router.query.source}`);
      }
    }
  }, [subscription, isProEligible]);

  return (
    <PageLayout authenticated>
      <BackHeader />

      <div className="container mx-auto mt-16 bg-accent px-8 lg:px-32">
        <div className="pt-8 text-3xl">Your subscription</div>
        <div className="divider"></div>
        <div className="flex flex-wrap gap-12">
          <div className="flex min-w-full flex-1 flex-col gap-1 md:min-w-0">
            <div>
              <span className="font-bold">{subscription.name}</span>
            </div>
            <div>
              <AccountItem
                text={`${subscription.pagesOrSlidesPerUpload} pages or slides per upload`}
                helpText={
                  'This is the maximum number of slides or pages you can select when generating flashcards.'
                }
              ></AccountItem>
            </div>
            <div>
              <AccountItem
                text={`${subscription.uploadsRemaining} / ${subscription.uploadsPerMonth} uploads remaining`}
                helpText={
                  'This is the total number of uploads you have remaining for the month.  Only successful uploads that generate flaschards will be counted towards your remaining uploads. If you have used all of your uploads for the month, you will need to wait until the Reset Date.'
                }
              ></AccountItem>
            </div>

            {isProEligible && !isPro && (
              <>
                <div>
                  <AccountItem
                    text={`${Math.max(
                      0,
                      subscription.practiceTestsRemaining
                    )} / ${
                      subscription.practiceTestsPerMonth
                    } practice tests remaining`}
                    helpText={
                      'This is the total number of practice tests you have remaining for the month.  If you have used all of your practice tests for the month, you will need to wait until the Reset Date.'
                    }
                  ></AccountItem>
                </div>
              </>
            )}

            {isPro && (
              <>
                <div>
                  <AccountItem
                    text={`Unlimited practice tests`}
                    helpText={
                      'As a Limbiks Pro subscriber there is no limit to how many practice tests you can create.'
                    }
                  ></AccountItem>
                </div>
              </>
            )}

            {isProEligible && !isPro && (
              <>
                <div>
                  <AccountItem
                    text={`${Math.max(0, subscription.questionsRemaining)} / ${
                      subscription.questionsPerMonth
                    } multiple choice questions remaining`}
                    helpText={
                      'This is the total number of multiple choice questions you have remaining for the month. If you have used all of your multiple choice questions for the month, you will need to wait until the Reset Date.'
                    }
                  ></AccountItem>
                </div>
              </>
            )}

            {isPro && (
              <>
                <div>
                  <AccountItem
                    text={`Unlimited multiple choice questions`}
                    helpText={
                      'As a Limbiks Pro subscriber there is no limit to how many multiple choice questions you can study.'
                    }
                  ></AccountItem>
                </div>
              </>
            )}
            <div>
              <AccountItem
                text={`${Math.max(0, subscription.ankiOcclusionsRemaining)} / ${
                  subscription.ankiOcclusionsPerMonth
                } Images remaining in`}
                helpText={
                  'This is the total number of multiple choice questions you have remaining for the month. If you have used all of your multiple choice questions for the month, you will need to wait until the Reset Date.'
                }
              ></AccountItem>
            </div>

            <div>
              <AccountItem
                text={`Remaining amounts will reset in ${getDaysFromNow(
                  subscription.uploadResetDate
                )} days`}
                helpText={
                  isPro
                    ? 'This is the number of days until uploads will reset to the full amount.  If you have used all of your uploads for the month, you will need to wait this many days.'
                    : 'This is the number of days until uploads, practice tests and multiple choice questions will reset to the full amount.  If you have reached your limits for the month, you will need to wait this many days.'
                }
              ></AccountItem>
            </div>

            <div>
              <AccountItem
                text={`Reset Date: ${subscription.uploadResetDate.toLocaleDateString()}`}
                helpText={
                  'This is the date your uploads remaining will reset to the full amount.  If you have used all of your uploads for the month, you will need to wait until this date. This date is based off of account creation date for free users and subscription start date for pro users. Reset date will be honored for cancelled subscribers until they have reached the end of their subscription period.'
                }
              ></AccountItem>
            </div>

            <CanceledBanner subscription={subscription} />
            {isProEligible && (
              <div className="mt-4">
                See the full breakdown of{' '}
                <span
                  onClick={() => {
                    trackEvent(analyticsInstance, 'account_click_pricing');
                  }}
                  className="link text-primary"
                >
                  <Link href={'/pricing'}>pricing and features</Link>
                </span>
              </div>
            )}
          </div>
          {!hasAnyStripeSubscription && isProEligible && (
            <UpgradeSubscription />
          )}
          {hasAnyStripeSubscription && <ManageSubscription />}
        </div>

        <div className="pt-16 text-3xl">Account Details</div>
        <div className="divider"></div>
        <div className="mb-12 flex flex-wrap gap-12">
          <div className="flex min-w-full flex-1 flex-col gap-1 md:min-w-0">
            {user?.displayName && (
              <AccountItem text={`Name: ${user?.displayName}`}></AccountItem>
            )}
            <AccountItem text={`Email: ${user?.email}`}></AccountItem>
            <div className="flex gap-2">
              <AccountItem
                text={`Limbiks Key: ${accessKey}`}
                copyText={accessKey}
                helpText="Use this Key to securely connect to Limbiks from external apps like the Practice Test Generator Anki-Addon"
              ></AccountItem>
              <div
                onClick={() => {
                  trackEvent(analyticsInstance, 'account_click_rotate_key');
                  rotateAccessKey();
                }}
                className="tooltip scale-90 cursor-pointer"
                data-tip={
                  'Click here to generate a new Limbiks Key.  Your existing Limbiks Key will no longer be usable.'
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height={24}
                  width={24}
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="white"
                    d="M12 2C17.5228 2 22 6.47715 22 12C22 14.1364 21.3301 16.1162 20.1889 17.741L17 12H20C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C14.1502 20 16.1023 19.1517 17.5398 17.7716L18.5379 19.567C16.7848 21.083 14.4995 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 7C13.6569 7 15 8.34315 15 10V11H16V16H8V11H9V10C9 8.34315 10.3431 7 12 7ZM14 13H10V14H14V13ZM12 9C11.4872 9 11.0645 9.38604 11.0067 9.88338L11 10V11H13V10C13 9.48716 12.614 9.06449 12.1166 9.00673L12 9Z"
                  ></path>
                </svg>
              </div>
            </div>
            <div className="mt-3 flex gap-2 align-middle">
              {' '}
              <span className="link text-primary">
                <a
                  onClick={() => {
                    trackEvent(analyticsInstance, 'account_click_settings');
                    openModal('settings');
                  }}
                >
                  View your account settings
                </a>
              </span>
            </div>
          </div>

          <DeleteAccount />
        </div>
        {user?.email === 'limbiks.root@gmail.com' && (
          <div className=" mb-8 flex w-96 flex-col gap-2 text-black">
            <div className="text-base-content">Sign in On Behalf</div>
            <input
              className="input"
              onChange={(ev) => setOnBehalfId(ev.target.value)}
              placeholder="uid"
              value={onBehalfId}
            />
            <button className="btn" onClick={() => signInOnBehalf(onBehalfId)}>
              Submit
            </button>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
